import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { SelectInput, UnifiedInput } from '../Input';
import { Grid, SelectChangeEvent } from '@mui/material';
import { useAppSelector } from '../../utils/redux';
import {
  buildingIdSelector,
  buildingsListSelector,
} from '../../redux/selectors/building';
import { CONTROLLER, GATEWAY, SENSOR } from '../../const/values';
import { useLocation } from 'react-router';
import { controllersSelectListSelector } from '../../redux/selectors/device';
import { companyIdSelector } from '../../redux/selectors/company';
import SelectSearch from '../Input/SelectSearch';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@mui/material';
import { ActionButton } from '../Button';
import ChannelModal from '../Modal/ChannelModal';
import axios from 'axios';

type Props = {
  handleSendData: (data: any, componentType: string) => void;
};

type ErrorProp = {
  deviceEUI?: string;
  applicationKey?: string;
  deviceApplicationEUI?: string;
  devicePin?: string;
  model?: string;
  serialNumber?: string;
  assetNumber?: string;
  ipAddress?: string;
  mac?: string;
  latitude?: string;
  longitude?: string;
  altitude?: string;
  status?: string;
  floor?: string;
  floorArea?: string;
  floorSubArea?: string;
};

const DeviceInformationCard = ({ handleSendData }: Props) => {
  const location = useLocation();
  const { t } = useTranslation();
  const buildings = useAppSelector(buildingsListSelector);
  const buildingId = useAppSelector(buildingIdSelector);
  const companyId = useAppSelector(companyIdSelector);
  const controllersIds = useAppSelector(controllersSelectListSelector);
  const [gatewayName, setGatewayName] = useState('');
  const [deviceEUI, setDeviceEUI] = useState('');
  const [applicationKey, setApplicationKey] = useState('');
  const [deviceApplicationEUI, setDeviceApplicationEUI] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [assetNumber, setAssetNumber] = useState('');
  const [errors, setErrors] = useState<ErrorProp>({});
  const [floor, setFloor] = useState('');
  const [floorArea, setFloorArea] = useState('');
  const [floorSubArea, setFloorSubArea] = useState('');
  const [building, setBuilding] = useState('');
  const [devices, setDevices] = useState<any>([]);
  const [channelA, setChannelA] = useState(false);
  const [channelB, setChannelB] = useState(false);
  const [isSelectChannel, setIsSelectChannel] = useState(false);
  const [channels, setChannels] = useState<any[]>();
  const [options, setOptions] = useState<any>([]);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [controllerDropdownOpen, setControllerDropdownOpen] = useState(false);

  const gatewayUrl = location.pathname.includes(GATEWAY);
  const controllerUrl = location.pathname.includes(CONTROLLER);
  const sensorUrl = location.pathname.includes(SENSOR);
  const componentType = !!gatewayUrl
    ? GATEWAY
    : !!controllerUrl
    ? CONTROLLER
    : SENSOR;

  useEffect(() => {
    if (buildingId) setBuilding(buildingId.toString());
  }, [buildingId, buildings]);

  const stateSetters: any = {
    name: setGatewayName,
    deviceEUI: setDeviceEUI,
    applicationKey: setApplicationKey,
    deviceApplicationEUI: setDeviceApplicationEUI,
    model: setModel,
    serialNumber: setSerialNumber,
    assetNumber: setAssetNumber,
    floorArea: setFloorArea,
    floorSubArea: setFloorSubArea,
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const setter = stateSetters[name];
    if (setter) {
      setter(value);
    }
  };

  const handleChangeBuilding = (event: SelectChangeEvent) => {
    setBuilding(event.target.value);
  };

  const handleChangeFloor = (event: SelectChangeEvent) => {
    setFloor(event.target.value);
  };

  const handleOpen = () => setIsSelectChannel(true);
  const handleClose = () => setIsSelectChannel(false);

  const sendData = () => {
    if (gatewayUrl) {
      const data = {
        name: gatewayName,
        building_id: building,
        company_id: companyId,
        floor,
        floor_area: floorArea,
        floor_sub_area: floorSubArea,
        status: true,
      };
      return handleSendData(data, componentType);
    }
    let channel = '';

    if (channelA && channelB) {
      channel = 'AB';
    } else if (channelB) {
      channel = 'B';
    } else {
      channel = 'A';
    }

    let deviceIds = [];
    if (devices.length > 0 && devices.length < 2) {
      deviceIds = devices.map((device: any) => `${device.value}_${channel}`);
    }
    if (devices.length > 1) {
      deviceIds = channels?.map((channel: any) => `${channel.device}`) || [];
    }

    const data = {
      asset_number: assetNumber,
      serial_number: serialNumber,
      eui: deviceEUI,
      application_key: applicationKey,
      dev_application_eui: deviceApplicationEUI,
      model,
      building_id: parseInt(building, 10),
      company_id: companyId,
      floor,
      floor_area: floorArea,
      floor_sub_area: floorSubArea,
      device_ids: deviceIds,
      channels: channel,
    };
    handleSendData(data, componentType);
  };

  const fetchItems = async (page = 1) => {
    if (loading) return;

    setLoading(true);

    try {
      const url = `/devices?type=controller&page=${page}&company_id=${companyId}&keyword=&sort=building_name`;
      const result = await axios.get(url);

      setOptions((prev: any) => {
        const idList = [
          ...result?.data.data?.items?.map((item: any) => ({
            value: item.id,
            label: item.asset_number,
            etage: item?.device_location?.floor,
            area: item?.device_location?.floor_area,
            subArea: item?.device_location?.floor_sub_area,
            devEui: item?.dev_application_eui,
          })),
        ];
        // Page 1 replaces existing data.
        if (page === 1) {
          return idList;
        }

        // Other pages are appended.
        return [...prev, ...idList];
      });

      setMeta(result?.data?.data?.meta);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (event: any) => {
    const listbox = event.currentTarget;

    const isNearBottom =
      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 50;

    if (!isNearBottom || loading || !meta) {
      return;
    }

    const hasNextPage = meta.current_page < meta.last_page;

    if (hasNextPage) {
      fetchItems(meta.current_page + 1);
    }
  };

  useEffect(() => {
    fetchItems(1);
  }, []);

  useEffect(() => {
    if (!controllerDropdownOpen || loading || !meta) return;

    const hasNextPage = meta.current_page < meta.last_page;
    if (!hasNextPage) return;

    const unselectedCount = options.filter(
      (option: any) =>
        !devices.some((device: any) => device?.value === option.value)
    ).length;

    if (unselectedCount < 8) {
      fetchItems(meta.current_page + 1);
    }
  }, [controllerDropdownOpen, loading, meta, options, devices]);

  const mergedOptions = [
    ...devices,
    ...options.filter(
      (option: any) =>
        !devices.some(
          (device: any) => String(device.value) === String(option.value)
        )
    ),
  ];

  return (
    <Container>
      <TitleContainer>{t('device-add-title')}</TitleContainer>
      {gatewayUrl && (
        <RowContainer container>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>{t('device-add-gateway-label')}</FieldDescription>
            <UnifiedInput
              placeholder={t('device-add-gateway-label')}
              hasBorder
              value={gatewayName}
              name="name"
              OnChange={handleChange}
              error={!!errors?.deviceEUI}
              errorMessage={errors?.deviceEUI}
            />
          </FieldContainer>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>
              {t('device-add-building-label')}
            </FieldDescription>
            <SelectInput
              values={buildings}
              value={building}
              OnChange={handleChangeBuilding}
            />
          </FieldContainer>
        </RowContainer>
      )}
      {!gatewayUrl && (
        <RowContainer container>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>{t('device-add-eui-label')}</FieldDescription>
            <UnifiedInput
              placeholder={t('device-add-eui-label')}
              hasBorder
              value={deviceEUI}
              name="deviceEUI"
              OnChange={handleChange}
              error={!!errors?.deviceEUI}
              errorMessage={errors?.deviceEUI}
            />
          </FieldContainer>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>{t('device-add-key-label')}</FieldDescription>
            <UnifiedInput
              placeholder={t('device-add-key-label')}
              hasBorder
              value={applicationKey}
              name="applicationKey"
              OnChange={handleChange}
              error={!!errors?.applicationKey}
              errorMessage={errors?.applicationKey}
            />
          </FieldContainer>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>{t('device-add-app-label')}</FieldDescription>
            <UnifiedInput
              placeholder={t('device-add-app-label')}
              hasBorder
              value={deviceApplicationEUI}
              name="deviceApplicationEUI"
              OnChange={handleChange}
              error={!!errors?.deviceApplicationEUI}
              errorMessage={errors?.deviceApplicationEUI}
            />
            {controllerUrl && (
              <div
                style={{ display: 'flex', justifyContent: 'center', gap: 8 }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{t('device-update-channel-use')} A</span>
                  <Checkbox
                    checked={channelA}
                    sx={{
                      color: 'grey',
                      '&.Mui-checked': {
                        color: '#FF0018',
                      },
                    }}
                    onChange={() => setChannelA((prev: boolean) => !prev)}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{t('device-update-channel-use')} B</span>
                  <Checkbox
                    checked={channelB}
                    sx={{
                      color: 'grey',
                      '&.Mui-checked': {
                        color: '#FF0018',
                      },
                    }}
                    onChange={() => setChannelB((prev: boolean) => !prev)}
                  />
                </div>
              </div>
            )}
          </FieldContainer>
          {sensorUrl && (
            <FieldContainer item xs={12} sm={12} md lg>
              <FieldDescription>{t('device-add-controller')}</FieldDescription>
              <SelectSearch
                multiple
                options={mergedOptions}
                value={devices}
                handleReachEnd={handleScroll}
                loading={loading}
                onOpen={() => setControllerDropdownOpen(true)}
                onClose={() => setControllerDropdownOpen(false)}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                OnChange={(e, newValue) => setDevices(e)}
                displayOption={(props, option) => {
                  return (
                    <li key={option.value} {...props}>
                      {option.etage} {option.area} {option.subArea} {option.dev}
                    </li>
                  );
                }}
              />
              {devices.length > 1 && (
                <ActionButton
                  buttonLabel={t('device-update-channel-button')}
                  color={'primary'}
                  OnClick={handleOpen}
                  style={{
                    borderRadius: 6,
                    marginTop: '8px',
                  }}
                />
              )}
              {devices.length > 0 && devices.length < 2 && (
                <div
                  style={{ display: 'flex', justifyContent: 'center', gap: 8 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{t('device-update-channel-use')} A</span>
                    <Checkbox
                      checked={channelA}
                      sx={{
                        color: 'grey',
                        '&.Mui-checked': {
                          color: '#FF0018',
                        },
                      }}
                      onChange={() => setChannelA((prev: boolean) => !prev)}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{t('device-update-channel-use')} B</span>
                    <Checkbox
                      checked={channelB}
                      sx={{
                        color: 'grey',
                        '&.Mui-checked': {
                          color: '#FF0018',
                        },
                      }}
                      onChange={() => setChannelB((prev: boolean) => !prev)}
                    />
                  </div>
                </div>
              )}
            </FieldContainer>
          )}
        </RowContainer>
      )}

      {!gatewayUrl && (
        <RowContainer container>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>{t('device-add-model-label')}</FieldDescription>
            <UnifiedInput
              placeholder={t('device-add-model-placeholder')}
              hasBorder
              value={model}
              name="model"
              OnChange={handleChange}
              error={!!errors?.model}
              errorMessage={errors?.model}
            />
          </FieldContainer>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>{t('device-add-serial-label')}</FieldDescription>
            <UnifiedInput
              placeholder={t('device-add-serial-label')}
              hasBorder
              value={serialNumber}
              name="serialNumber"
              OnChange={handleChange}
              error={!!errors?.serialNumber}
              errorMessage={errors?.serialNumber}
            />
          </FieldContainer>
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>{t('device-add-asset-label')}</FieldDescription>
            <UnifiedInput
              placeholder={t('device-add-asset-label')}
              hasBorder
              value={assetNumber}
              name="assetNumber"
              OnChange={handleChange}
              error={!!errors?.assetNumber}
              errorMessage={errors?.assetNumber}
            />
          </FieldContainer>
        </RowContainer>
      )}
      <RowContainer container>
        {!gatewayUrl && (
          <FieldContainer item xs={12} sm={12} md lg>
            <FieldDescription>
              {t('device-add-building-label')}
            </FieldDescription>
            <SelectInput
              values={buildings}
              value={building}
              OnChange={handleChangeBuilding}
            />
          </FieldContainer>
        )}
        <FieldContainer item xs={12} sm={12} md lg>
          <FieldDescription>{t('device-add-floor-label')}</FieldDescription>
          <UnifiedInput
            placeholder={t('device-add-floor-label')}
            hasBorder
            value={floor}
            name="floor"
            OnChange={handleChangeFloor}
            error={!!errors?.floor}
            errorMessage={errors?.floor}
          />
        </FieldContainer>
        <FieldContainer item xs={12} sm={12} md lg>
          <FieldDescription>
            {t('device-add-floor-area-label')}
          </FieldDescription>
          <UnifiedInput
            placeholder={t('device-add-floor-area-label')}
            hasBorder
            value={floorArea}
            name="floorArea"
            OnChange={handleChange}
            error={!!errors?.floorArea}
            errorMessage={errors?.floorArea}
          />
        </FieldContainer>
        <FieldContainer item xs={12} sm={12} md lg>
          <FieldDescription>{t('device-add-floor-sub-label')}</FieldDescription>
          <UnifiedInput
            placeholder={t('device-add-floor-sub-label')}
            hasBorder
            value={floorSubArea}
            name="floorSubArea"
            OnChange={handleChange}
            error={!!errors?.floorSubArea}
            errorMessage={errors?.floorSubArea}
          />
        </FieldContainer>
      </RowContainer>
      <div
        style={{
          padding: '8px 16px',
          background: '#FF0018',
          borderRadius: 6,
          display: 'flex',
          alignSelf: 'baseline',
        }}
      >
        <div
          style={{
            color: 'white',
            fontSize: 14,
            fontWeight: '600',
            lineHeight: '20px',
            cursor: 'pointer',
          }}
          onClick={sendData}
        >
          {t('button-add')}
        </div>
      </div>
      <ChannelModal
        open={isSelectChannel}
        handleClose={handleClose}
        devices={devices}
        setChannels={setChannels}
      />
    </Container>
  );
};

const Container = styled('div')`
  padding: 16px 20px;
  border-radius: 10px;
  border: 0.5px #4a4a4a solid;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleContainer = styled('div')`
  color: #4a4a4a;
  font-weight: 600;
  letter-spacing: 0.02px;
`;

const RowContainer = styled(Grid)`
  gap: 12px;
  display: flex;
`;

const FieldContainer = styled(Grid)`
  flex-direction: column;
  gap: 6;
  display: flex;
`;

const FieldDescription = styled('div')`
  color: #4a4a4a;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  word-wrap: break-word;
`;

export default DeviceInformationCard;
